import { Button, ClickEvent, DataSourceMode, SlideoutDecorator, Table, TableContentsChangedEvent, TableRow, TableRowDisplayEvent, TableSelectionEvent, Textbox } from "@mcleod/components";
import { AutogenLayoutOriginDestinationRateTable } from "./autogen/AutogenLayoutOriginDestinationRateTable";
import { LocationByTypeProps, LocationByTypeUtil } from "../LocationByTypeUtil";
import { OriginDestinationRate } from "./OriginDestinationRate";
import { ModelRow, StringUtil } from "@mcleod/core";
import { RateUtil } from "../RateUtil";
import { RateAdjustmentStepper } from "./RateAdjustmentStepper";
import { FilterPanel, SecondaryFilterProps } from "@mcleod/dispatch";

const ACTIVE: SecondaryFilterProps = {
    id: "active",
    caption: "Active",
    filter: (row: ModelRow) => {
        const currentTimestamp = new Date();
        currentTimestamp.setHours(23,59,59);
        const expireTimestamp = new Date(row.getDate("expiration_date"));
        return row.getDate("expiration_date") == null || currentTimestamp < expireTimestamp;
    }
}

const EXPIRED: SecondaryFilterProps = {
    id: "expired",
    caption: "Expired",
    filter: (row: ModelRow) => {
        const currentTimestamp = new Date();
        currentTimestamp.setHours(23,59,59);
        const expireTimestamp = new Date(row.getDate("expiration_date"));
        return row.getDate("expiration_date") != null && currentTimestamp >= expireTimestamp;
    }
}

export class OriginDestinationRateTable extends AutogenLayoutOriginDestinationRateTable {

    private _customerId = null;
    private _rateId = null;
    private filterPanel: FilterPanel;

    onLoad(): void | Promise<void> {
        this.tableRates.onEditLayoutLoaded = (decorator, tableRow) => {
            const odRateLayout = decorator.layout as OriginDestinationRate;
            odRateLayout.customerId = this.customerId;
            decorator.multiButton.dropdownItems = null;
            const isSearch = this.mainDataSource.mode === DataSourceMode.SEARCH;
            if (isSearch) {
                decorator.labelTitle.text = 'Search Rates'
            }
            decorator.multiButton.caption =  (isSearch)
                ? 'Apply Filters'
                : 'Apply Edits';
            decorator.doAfterSlideOut = (decoratorAfterEdit) => {
                tableRow.data.set("orig_location_name", null);
                tableRow.data.set("inter_location_name", null);
                tableRow.data.set("dest_location_name", null);
                this.syncLocationByType(tableRow);
                RateUtil.setCountyValuesIfNeeded(tableRow.data, "N", "orig_code", "orig_city_name", "orig_state", "orig_value");
                RateUtil.setCountyValuesIfNeeded(tableRow.data, "N", "intermediate_code", "intermediate_city_name", "intermediate_state", "intermediate_value");
                RateUtil.setCountyValuesIfNeeded(tableRow.data, "N", "dest_code", "dest_city_name", "dest_state", "dest_value");
                tableRow.displayComponentValues();
            }

            //handle shuffling of comments between parent and child
            const editSlideout = decorator.layout as OriginDestinationRate;
            const superOnSave = decorator.onSave;
            decorator.doAfterSlideIn = (_) => {
                if (!tableRow.data.isNull('comments')) {
                    const comments = tableRow.data.get('comments');
                    editSlideout.sourceComments.setRowsAndMode(DataSourceMode.UPDATE, comments);
                    editSlideout.updateTabCountLabel(editSlideout.tabComments, editSlideout.layoutComments.tableComments);
                }
            }
            decorator.onSave = (updatedRow) => {
                if (decorator.layout.mainDataSource.validate()) {
                    const comments = editSlideout.layoutComments?.tableComments?.data;
                    updatedRow.set('comments', comments);
                    if (updatedRow.getBoolean('is_multistop')) odRateLayout.setMultistopIntermediateFromTable();
                    superOnSave(updatedRow);
                }
            }
        }
        this.enableButtonAdjustRate();
        this.filterPanel = new FilterPanel([ACTIVE, EXPIRED], this.tableRates, this.panelSecondaryFilters);
    }

    buttonAdjustRateOnClick(event: ClickEvent) {
        const selectedModelRows = this.tableRates.selectedRows.map(row => row.data as ModelRow)
        RateAdjustmentStepper.showAsSlideout(selectedModelRows, this.mainDataSource.parentDataSource);
    }

    tableRatesOnSelect(event: TableSelectionEvent) {
        this.enableButtonAdjustRate();
    }

    enableButtonAdjustRate() {
        if (this.tableRates.selectedRows.length == 0) {
            this.buttonAdjustRate.enabled = false;
            this.buttonAdjustRate.disabledTooltip = "Select a row to adjust rate";
        }
        else {
            this.buttonAdjustRate.enabled = true;
        }
    }

    buttonSearchOnClick(event: ClickEvent) {
        this.tableRates.allRows[0].showEditLayout();
    }

    buttonAddOnClick(event: ClickEvent) {
        const saveButton = this.createSaveButton();
        SlideoutDecorator.decorateAndSlideIn('lme/dispatch/rates/OriginDestinationRate', {
            fillVerticalSpace: true,
            width: '75%',
            title: 'Add Origin/Destination Rate',
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: saveButton,
            layoutLoadListeners: async (event) => {
                const layout = event.target as OriginDestinationRate;
                layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
                layout.activeRow.addAfterFieldUpdateListener((event) => {
                    layout.syncUIControls();
                });
                layout.syncUIControls();
                layout.syncRateMethod();
                layout.syncOverrideDistance();
                layout.syncMultistopTable();
            },
            doAfterSlideIn: async (decorator: SlideoutDecorator) => {
                (decorator.layout as OriginDestinationRate).customerId = this.customerId;

                saveButton.addClickListener(event => {
                    saveButton.busy = true;
                    const layout = decorator.layout as OriginDestinationRate;
                    if (layout.mainDataSource.validate()) {
                        const odRate = layout.activeRow;
                        const comments = layout.sourceComments.data;
                        decorator.slideOut();
                        if (!StringUtil.isEmptyString(this.rateId)) {
                            odRate.set('rate_id', this.rateId);
                        }
                        odRate.set('comments', comments);
                        if (odRate.getBoolean('is_multistop')) layout?.setMultistopIntermediateFromTable();
                        RateUtil.setCountyValuesIfNeeded(odRate, "N", "orig_code", "orig_city_name", "orig_state", "orig_value");
                        RateUtil.setCountyValuesIfNeeded(odRate, "N", "intermediate_code", "intermediate_city_name", "intermediate_state", "intermediate_value");
                        RateUtil.setCountyValuesIfNeeded(odRate, "N", "dest_code", "dest_city_name", "dest_state", "dest_value");
                        const result = this.tableRates.addRow(odRate, null, { display: true, addToData: true });
                        this.refreshRow(result.row);
                    }
                    saveButton.busy = false;
                });
            }
        });
    }

    tableRowOnDblClick(event: ClickEvent) {
        const tableRow = event.target as TableRow;
        if (tableRow.showEditLayout) {
            tableRow.showEditLayout();
        }
    }

    createSaveButton() {
        return new Button({
            id: "odRateSaveButton",
            caption: "Add",
            backgroundColor: "primary",
            minWidth: 128,
            borderWidth: 0,
            rowBreak: false
        });
    }

    tableRatesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        this.refreshRow(tableRow);
    }

    refreshRow(row: TableRow) {
        this.syncLocationByType(row);
        this.syncRate(row);
    }

    syncLocationByType(tableRow: TableRow) {
        RateUtil.syncLocationByType(tableRow);
    }

    syncRate(tableRow: TableRow) {
        RateUtil.syncRate(tableRow);
    }

    get customerId() {
        return this._customerId;
    }

    set customerId(value: string) {
        this._customerId = value
    }

    public get rateId() {
        return this._rateId;
    }
    public set rateId(value) {
        this._rateId = value;
    }
}
